import { Chip, Stack, TextField, Typography } from '@mui/material';
import React from 'react';

interface ChipListFieldProps {
  readonly label: string;
  readonly value: readonly string[];
  readonly onChange: (next: string[]) => void;
  /** Pending (not-yet-committed) text. Owned by the parent so a Save action can
   * persist whatever is typed without the user having to commit it first. */
  readonly draft: string;
  readonly onDraftChange: (value: string) => void;
  readonly placeholder?: string;
  readonly helperText?: string;
  readonly disabled?: boolean;
}

/**
 * Edits a list of free-text string entries as removable chips. Pressing Enter
 * commits an entry; duplicates (case-insensitive) are ignored. There is no Add
 * button and no commit-on-blur (which would reflow the layout) — the parent
 * owns the draft, so any uncommitted text is persisted when the surrounding
 * form is saved.
 */
export function ChipListField({
  label,
  value,
  onChange,
  draft,
  onDraftChange,
  placeholder,
  helperText,
  disabled = false
}: ChipListFieldProps) {
  const commit = () => {
    const entry = draft.trim();
    if (entry === '') return;
    const exists = value.some(item => item.toLowerCase() === entry.toLowerCase());
    if (!exists) onChange([...value, entry]);
    onDraftChange('');
  };

  const remove = (entry: string) => onChange(value.filter(item => item !== entry));

  return (
    <Stack spacing={2}>
      <Typography variant="labelSmall" sx={{ fontWeight: 600, color: 'text.secondary' }}>
        {label}
      </Typography>
      <TextField
        fullWidth
        size="small"
        placeholder={placeholder}
        value={draft}
        disabled={disabled}
        onChange={event => onDraftChange(event.target.value)}
        onKeyDown={event => {
          if (event.key === 'Enter') {
            event.preventDefault();
            commit();
          }
        }}
      />
      {value.length > 0 && (
        <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1 }}>
          {value.map(entry => (
            <Chip
              key={entry}
              label={entry}
              size="small"
              onDelete={disabled ? undefined : () => remove(entry)}
              sx={{
                // The default chip renders near-invisible on the admin theme
                // (white text on a faint grey). Force readable foreground for
                // both the label and the delete (×) button, and give the chip
                // a defined fill + border. Theme tokens keep it correct in dark
                // mode.
                color: theme => theme.brandPalette.foreground.neutral,
                backgroundColor: theme => theme.brandPalette.background.faded,
                border: theme => `1px solid ${theme.brandPalette.border.neutralFaded}`,
                '& .MuiChip-deleteIcon': {
                  color: theme => theme.brandPalette.foreground.neutral,
                  '&:hover': { color: theme => theme.brandPalette.foreground.neutralFaded }
                }
              }}
            />
          ))}
        </Stack>
      )}
      {helperText != null && (
        <Typography
          variant="labelXSmall"
          sx={{ color: theme => theme.brandPalette.foreground.neutralFaded }}
        >
          {helperText}
        </Typography>
      )}
    </Stack>
  );
}
